// Right-click context menu for a message row. Mounted as a
// controlled Radix DropdownMenu anchored at the cursor position.
//
// Why a controlled DropdownMenu and not a true ContextMenu primitive:
// `@radix-ui/react-context-menu` isn't a dep yet, and DropdownMenu
// gives us identical keyboard semantics with the same a11y story.
// The trick is the invisible `<DropdownMenuTrigger>` we anchor to
// the cursor's exact (x, y) so the menu floats from the click.

import {
  Bell,
  CornerUpRight,
  Copy,
  Edit3,
  ExternalLink,
  Hash,
  MessageSquarePlus,
  Pin,
  Plus,
  Reply,
  Sparkles,
  Trash2,
  Volume2,
} from "lucide-react";
import type { ReactNode } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
} from "@link/ui";

import type { MessageView } from "../../types/ui";

interface Props {
  message: MessageView;
  /** Cursor position used to anchor the floating menu. */
  position: { x: number; y: number } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional callbacks; actions without an implementation stay out of the menu. */
  onAddReaction?: (m: MessageView) => void;
  onEdit?: (m: MessageView) => void;
  onReply?: (m: MessageView) => void;
  onForward?: (m: MessageView) => void;
  onCreateThread?: (m: MessageView) => void;
  onCopyText?: (m: MessageView) => void;
  onPin?: (m: MessageView) => void;
  onApps?: (m: MessageView) => void;
  onMarkUnread?: (m: MessageView) => void;
  onCopyLink?: (m: MessageView) => void;
  onSpeak?: (m: MessageView) => void;
  onDelete?: (m: MessageView) => void;
  onCopyId?: (m: MessageView) => void;
  /** True when the local user authored the message — gates Edit / Delete. */
  authoredByMe?: boolean;
}

export function MessageContextMenu({
  message,
  position,
  open,
  onOpenChange,
  onAddReaction,
  onEdit,
  onReply,
  onForward,
  onCreateThread,
  onCopyText,
  onPin,
  onApps,
  onMarkUnread,
  onCopyLink,
  onSpeak,
  onDelete,
  onCopyId,
  authoredByMe,
}: Props) {
  // Default copy-text behaviour writes the message body to the
  // clipboard. The pinned-list popover uses the same canonical
  // body so this is safe for non-secret messages; secret messages
  // are special-cased by the caller (they pass an `onCopyText`
  // that copies the placeholder) when surfaced through here.
  const handleCopyText = (): void => {
    if (onCopyText !== undefined) {
      onCopyText(message);
      return;
    }
    void navigator.clipboard.writeText(message.body);
  };

  const handleCopyLink = (): void => {
    if (onCopyLink !== undefined) {
      onCopyLink(message);
      return;
    }
    void navigator.clipboard.writeText(
      `${window.location.origin}/m/${message.xid}`,
    );
  };

  const handleCopyId = (): void => {
    if (onCopyId !== undefined) {
      onCopyId(message);
      return;
    }
    void navigator.clipboard.writeText(message.xid);
  };

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      {/* Invisible 1px trigger floated at the cursor — Radix anchors
          the menu to it. */}
      <DropdownMenuTrigger asChild>
        <span
          aria-hidden
          style={{
            position: "fixed",
            top: position?.y ?? 0,
            left: position?.x ?? 0,
            width: 1,
            height: 1,
            pointerEvents: "none",
          }}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        sideOffset={4}
        collisionPadding={8}
        sticky="always"
        className="max-h-[calc(100dvh-1rem)] w-60 overflow-y-auto overscroll-contain border-cloud-200 bg-white text-cloud-700 shadow-xl"
      >
        {onAddReaction !== undefined ? (
          <Row
            icon={<Plus className="h-4 w-4 text-strawberry-500" />}
            label="Add Reaction"
            onSelect={() => onAddReaction(message)}
          />
        ) : null}
        {authoredByMe === true && onEdit !== undefined && (
          <Row
            icon={<Edit3 className="h-4 w-4 text-strawberry-500" />}
            label="Edit Message"
            onSelect={() => onEdit(message)}
          />
        )}
        {onReply !== undefined ? <Row icon={<Reply className="h-4 w-4 text-strawberry-500" />} label="Reply" onSelect={() => onReply(message)} /> : null}
        {onForward !== undefined ? <Row icon={<CornerUpRight className="h-4 w-4 text-strawberry-500" />} label="Forward" onSelect={() => onForward(message)} /> : null}
        {onCreateThread !== undefined ? <Row icon={<MessageSquarePlus className="h-4 w-4 text-strawberry-500" />} label="Create Thread" onSelect={() => onCreateThread(message)} /> : null}
        <Row
          icon={<Copy className="h-4 w-4 text-strawberry-500" />}
          label="Copy Text"
          onSelect={handleCopyText}
        />
        {onPin !== undefined ? <Row icon={<Pin className="h-4 w-4 text-strawberry-500" />} label={message.pinned === true ? "Unpin Message" : "Pin Message"} onSelect={() => onPin(message)} /> : null}
        {onApps !== undefined ? <Row icon={<Sparkles className="h-4 w-4 text-strawberry-500" />} label="Apps" onSelect={() => onApps(message)} /> : null}
        {onMarkUnread !== undefined ? <Row icon={<Bell className="h-4 w-4 text-strawberry-500" />} label="Mark Unread" onSelect={() => onMarkUnread(message)} /> : null}
        <Row
          icon={<ExternalLink className="h-4 w-4 text-strawberry-500" />}
          label="Copy Message Link"
          onSelect={handleCopyLink}
        />
        {onSpeak !== undefined ? <Row icon={<Volume2 className="h-4 w-4 text-strawberry-500" />} label="Speak Message" onSelect={() => onSpeak(message)} /> : null}
        {authoredByMe === true && onDelete !== undefined && (
          <>
            <DropdownMenuSeparator className="bg-cloud-200" />
            <Row
              icon={<Trash2 className="h-4 w-4 text-red-500" />}
              label="Delete Message"
              destructive
              onSelect={() => onDelete(message)}
            />
          </>
        )}
        <DropdownMenuSeparator className="bg-cloud-200" />
        <Row
          icon={<Hash className="h-4 w-4 text-strawberry-500" />}
          label="Copy Message ID"
          onSelect={handleCopyId}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Row({
  icon,
  label,
  destructive,
  onSelect,
}: {
  icon: ReactNode;
  label: string;
  destructive?: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem
      onSelect={onSelect}
      className={cn(
        "gap-3 px-3 py-1.5 text-sm focus:bg-strawberry-50 focus:text-strawberry-700",
        destructive
          ? "text-red-600 focus:bg-red-50 focus:text-red-700"
          : "text-cloud-700",
      )}
    >
      {icon}
      <span>{label}</span>
    </DropdownMenuItem>
  );
}
